import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Loading from "./Components/Loading";
import Header from "./Components/Header";
import ScrollTop from "./Components/ScrollToTop";

const PortalImport = lazy(() => import("./Screen/Home"));
const PortalListDetails = lazy(() => import("./Screen/Portal.List.Details"));
const Session = lazy(() => import("./Screen/Session"));
const ListingSummary = lazy(() => import("./Screen/Listing.Summary"));
const ListingQualityRating = lazy(() =>
  import("./Screen/ListingQuality.Rating")
);
const ProductGroupDetails = lazy(() => import("./Screen/ProductGroup.Details"));
const OptsInOut = lazy(() => import("./Screen/OptsIn.OptsOut"));

//Selling Price
const TransferPriceSummary = lazy(() =>
  import("./Screen/SellingPrice/TransferPriceSummary")
);
const ProductStyleColor = lazy(() =>
  import("./Screen/SellingPrice/ProductStyleColor")
);
const OtherExpenses = lazy(() => import("./Screen/SellingPrice/OtherExpenses"));
const ChangePlannedMargin = lazy(() =>
  import("./Screen/SellingPrice/ChangePlannedMargin")
);
const PlannedMarginChangeLog = lazy(() =>
  import("./Screen/SellingPrice/PlannedMarginChangeLog")
);
const PortalCardRateSummary = lazy(() =>
  import("./Screen/SellingPrice/PortalRateCardSummary")
);
const RateCardDependent = lazy(() =>
  import("./Screen/SellingPrice/RateCardDependent")
);
const RateCardDependentAnalysis = lazy(() =>
  import("./Screen/SellingPrice/RateCardAnalysis")
);

const CostPriceSummary = lazy(() =>
  import("./Screen/CostSellingPrice/CostSummary")
);

const CostPriceSetting = lazy(() =>
  import("./Screen/CostSellingPrice/CostPriceSetting")
);

const CostPriceChangeLog = lazy(() =>
  import("./Screen/CostSellingPrice/CostPriceChangeLog")
);

const CostGroup = lazy(() => import("./Screen/CostSellingPrice/CostGroup"));
const PriceBand = lazy(() => import("./Screen/CostSellingPrice/PriceBand"));

const ImportSku = lazy(() => import("./Screen/ImportSku"));
const CostPriceDetails = lazy(() =>
  import("./Screen/CostSellingPrice/CostPriceDetails")
);

const ConsumptionPriceHistory = lazy(() =>
  import("./Screen/CostSellingPrice/ConsumptionPriceHistory")
);

const AddCADMarker = lazy(() =>
  import("./Screen/CostSellingPrice/AddCADMarker")
);
const MasterGroupSummary = lazy(() => import("./Screen/MasterGroupSummary"));

const GetCostPriceChangeLog = lazy(() =>
  import("./Screen/CostSellingPrice/GetCostPrice")
);

const GetCostPrice = lazy(() =>
  import("./Screen/CostSellingPrice/GetCostPrices")
);

const ChangePriceComponentDetails = lazy(() =>
  import("./Screen/CostSellingPrice/ChangePriceComponentDetails")
);

const AddMasterGroup = lazy(() => import("./Screen/AddMasterGroup"));

export const IMAGE_URI = process.env.REACT_APP_IMG_URI;

const App = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Handle Click Outside
  const sideNavRef = useRef(null);

  useEffect(() => {
    // Add event listener to the document object
    document.addEventListener("mousedown", (event) =>
      handleClickOutside(event, setIsOpen)
    );

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", (event) =>
        handleClickOutside(event, setIsOpen)
      );
    };
  }, []);

  const handleClickOutside = (event, setIsOpen) => {
    if (sideNavRef.current && !sideNavRef.current.contains(event.target)) {
      // Clicked outside the side navigation bar, close it
      setIsOpen(false);
    }
  };

  const sharedProps = {
    isOpen,
    setIsOpen,
    sideNavRef,
  };

  return (
    <Router>
      <Suspense
        fallback={
          <Loading color={"rgb(255, 180, 85)"} size={42} height={true} />
        }
      >
        <Header setIsOpen={setIsOpen} />
        <ScrollTop />
        <Routes>
          <Route path="/" element={<Session {...sharedProps} />} />
          <Route
            path="/portal-list"
            element={<PortalImport {...sharedProps} />}
          />
          <Route
            path="/portal-list-details"
            element={<PortalListDetails {...sharedProps} />}
          />

          {/* Selling Price Routes */}
          <Route
            path="/selling-price/transfer-price-summary"
            element={<TransferPriceSummary {...sharedProps} />}
          />
          <Route
            path="/selling-price/product-style-color"
            element={<ProductStyleColor {...sharedProps} />}
          />
          <Route
            path="/selling-price/other-expenses"
            element={<OtherExpenses {...sharedProps} />}
          />
          <Route
            path="/selling-price/change-planned-margin"
            element={<ChangePlannedMargin {...sharedProps} />}
          />
          <Route
            path="/selling-price/view-change-log"
            element={<PlannedMarginChangeLog {...sharedProps} />}
          />
          <Route
            path="/selling-price/rate-card-summary"
            element={<PortalCardRateSummary {...sharedProps} />}
          />
          <Route
            path="/selling-price/rate-card-dependents"
            element={<RateCardDependent {...sharedProps} />}
          />
          <Route
            path="/selling-price/rate-card-dependents-analysis"
            element={<RateCardDependentAnalysis {...sharedProps} />}
          />

          {/* Cost Selling Price Routes Starts here */}
          <Route
            path="/cost-price/summary"
            element={<CostPriceSummary {...sharedProps} />}
          />
          <Route
            path="/cost-price/cost-price-settings"
            element={<CostPriceSetting {...sharedProps} />}
          />
          <Route
            path="/cost-price/cost/group"
            element={<CostGroup {...sharedProps} />}
          />
          <Route
            path="/cost-price/price/band"
            element={<PriceBand {...sharedProps} />}
          />
          <Route
            path="/cost-price/components/details"
            element={<CostPriceDetails {...sharedProps} />}
          />
          <Route
            path="/cost-price/edit-change-price/details"
            element={<CostPriceDetails {...{ ...sharedProps, edit: true }} />}
          />
          <Route
            path="/cost-price/consumption/price/history/:id"
            element={<ConsumptionPriceHistory {...sharedProps} />}
          />
          <Route
            path="/cost-price/cost-price-log"
            element={<CostPriceChangeLog {...sharedProps} />}
          />
          <Route
            path="/cost-price/add-cad-marker"
            element={<AddCADMarker {...sharedProps} />}
          />
          <Route
            path="/cost-price/change-log"
            element={<GetCostPriceChangeLog {...sharedProps} />}
          />
          <Route
            path="/cost-price/get-cost-price"
            element={<GetCostPrice {...sharedProps} />}
          />
          <Route
            path="/cost-price/change-price-component-details"
            element={<ChangePriceComponentDetails {...sharedProps} />}
          />
          {/* Cost Selling Price Routes ends here */}

          {/* Portal List Routes starts here */}
          <Route
            path="/listing/summary"
            element={<ListingSummary {...sharedProps} />}
          />
          <Route
            path="/add-master-group"
            element={<AddMasterGroup {...sharedProps} />}
          />
          <Route path="/import/sku" element={<ImportSku {...sharedProps} />} />
          <Route
            path="/listing-quality-rating/:id?"
            element={<ListingQualityRating {...sharedProps} />}
          />
          <Route
            path="/product-group-details/:id"
            element={<ProductGroupDetails {...sharedProps} />}
          />
          <Route path="/opts-inout" element={<OptsInOut {...sharedProps} />} />
          <Route
            path="/listing/master-group-summary"
            element={<MasterGroupSummary {...sharedProps} />}
          />
          {/* Portal List Routes ends here */}
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
